import {
    Badge,
    Box,
    Button,
    Center,
    Container,
    Group,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";
import {
    IconClock,
    IconCurrencyDollar,
    IconKey,
    IconLock,
    IconNorthStar,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { RequestAccessModal } from "src/components/RequestAccessModal";
import { useUser, useUserDatasources } from "src/hooks/useUser";
import { getRuntimeEnvironment } from "src/utils/runtimeEnvironment";
import { useNotAuthorizedUser } from "src/utils/useAdmin";

export const Route = createFileRoute("/_layout-nosidebar/unauthorized")({
    component: UnauthorizedRoute,
});
export function UnauthorizedRoute() {
    const environment = getRuntimeEnvironment();

    const [countdown, setCountdown] = useState(3);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const navigate = useNavigate();
    // useNotAuthorizedUser() already fetches /v1/user - reuse its pending access
    // requests instead of a separate call to /v1/user/access-requests.
    const { data: user } = useUser();
    const hasPendingRequests = (user?.access_requests?.length ?? 0) > 0;
    // Names for the pending knowledge bases come from the configuration-backed
    // datasource list; /v1/user only carries their ids.
    const { data: userDatasources } = useUserDatasources();
    const pendingDatasourceNames = useMemo(() => {
        const pendingIds = new Set(
            (user?.access_requests ?? []).flatMap((request) =>
                (request.requested_datasources ?? []).map((ds) => ds.id),
            ),
        );
        return (userDatasources ?? [])
            .filter((ds) => pendingIds.has(ds.id))
            .map((ds) => ds.name)
            .toSorted((a, b) => a.localeCompare(b));
    }, [user, userDatasources]);
    const isNotAuthorized = useNotAuthorizedUser();

    const shouldRedirectToProduction = environment === "dev" || environment === "qa";
    const GUI_URL_PROD = window.location.origin
        .replace("mosaic-qa.", "mosaic.")
        .replace("mosaic-dev.", "mosaic.");
    console.info("Prod instance", GUI_URL_PROD);

    useEffect(() => {
        if (!isNotAuthorized) {
            navigate({ to: "/" });
            return;
        }

        if (shouldRedirectToProduction) {
            setIsRedirecting(true);

            const countdownInterval = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        window.location.href = GUI_URL_PROD;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 2000);

            return () => clearInterval(countdownInterval);
        }

        return undefined;
    }, [shouldRedirectToProduction, isNotAuthorized, navigate, GUI_URL_PROD]);

    if (isRedirecting) {
        return (
            <Center py="xl" style={{ height: "100%" }}>
                <Container size="sm">
                    <Box ta="center" py="xl">
                        <Title>Redirecting to Knowledge Search</Title>
                        <Text mb="md" size="lg">
                            You are being redirected to the Production Environment in {countdown}{" "}
                            seconds...
                        </Text>
                        <Text size="sm" mb="lg">
                            This is a {environment.toUpperCase()} environment. For the latest stable
                            version, please use the production environment.
                        </Text>
                        <Button component="a" href={GUI_URL_PROD} variant="filled">
                            Go to Production Now
                        </Button>
                    </Box>
                </Container>
            </Center>
        );
    }

    return (
        <Center py="xl" style={{ height: "100%" }}>
            <Container size="sm">
                <Title variant="heading1">Knowledge Search</Title>
                <Text mt={4} size="xl">
                    We help you find the information you need.
                </Text>
                <SimpleGrid mt={50} cols={{ base: 1, md: 3 }} spacing={30}>
                    {features.map((feature) => (
                        <div key={feature.title}>
                            <ThemeIcon variant="outline" size={50} radius={50}>
                                <feature.icon size={26} stroke={1.5} />
                            </ThemeIcon>
                            <Text mt="sm" mb={7}>
                                {feature.title}
                            </Text>
                            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                                {feature.description}
                            </Text>
                        </div>
                    ))}
                </SimpleGrid>
                <Stack mt={50}>
                    {hasPendingRequests && (
                        <Stack gap={6}>
                            <Text size="xl" fw={600}>
                                You already requested access - we're working on it!
                            </Text>
                            <Text size="sm" c="dimmed">
                                You can still request access to more knowledge bases.
                            </Text>
                            <Group gap={6} data-testid="pending-access-requests">
                                {pendingDatasourceNames.map((name) => (
                                    <Badge
                                        key={name}
                                        variant="light"
                                        color="yellow"
                                        leftSection={<IconClock size={12} />}
                                    >
                                        {name}
                                    </Badge>
                                ))}
                            </Group>
                        </Stack>
                    )}
                    <RequestAccessModal>
                        <Button size="md" variant="filled" leftSection={<IconKey size={20} />}>
                            Request Access
                        </Button>
                    </RequestAccessModal>
                </Stack>
            </Container>
        </Center>
    );
}

const features = [
    {
        icon: IconCurrencyDollar,
        title: "Increase Productivity",
        description:
            "Leverage our Knowledge Search tool to handle Salesforce cases and field service reports effortlessly. Save time by getting instant answers, summaries, and insights without reading through lengthy documents.",
    },
    {
        icon: IconLock,
        title: "Enhanced Privacy",
        description:
            "Your data security is our top priority. With no tracking, no cookies, and complete local storage, our tool ensures that your sensitive information remains confidential and protected.",
    },
    {
        icon: IconNorthStar,
        title: "Superior Support Experience",
        description:
            "Experience unparalleled efficiency with a tool designed to help you provide exceptional support. Our copilot tool enhances your ability to assist end users, ensuring a seamless and productive workflow.",
    },
];
